import { useEffect, useMemo, useRef, useState } from "react";
import { useGlobalSnackbar } from "../../context/SnackbarContext";
import { socket } from "src/sockets/socket-vr";

export interface IUseWatchVrNotificationProps<TPayload> {
    notificationTypes: string[];
    disableSnackbarAlert?: boolean;
    onNotificationArrived?: (notification?: TPayload) => void;
    buildSnackbarMessage?: (notification: TPayload) => string;
}

export function useWatchVrNotification<TPayload>({
    notificationTypes,
    disableSnackbarAlert = false,
    onNotificationArrived,
    buildSnackbarMessage,
}: IUseWatchVrNotificationProps<TPayload>): {
    lastNotification: TPayload | null;
    clearLastNotification: () => void;
} {
    const [lastNotification, setLastNotification] = useState<TPayload | null>(null);
    const { showSnackbar } = useGlobalSnackbar();

    const disableSnackbarAlertRef = useRef(disableSnackbarAlert);
    const onNotificationArrivedRef = useRef(onNotificationArrived);
    const buildSnackbarMessageRef = useRef(buildSnackbarMessage);

    useEffect(() => { disableSnackbarAlertRef.current = disableSnackbarAlert; }, [disableSnackbarAlert]);
    useEffect(() => { onNotificationArrivedRef.current = onNotificationArrived; }, [onNotificationArrived]);
    useEffect(() => { buildSnackbarMessageRef.current = buildSnackbarMessage; }, [buildSnackbarMessage]);

    const normalizedTypes = useMemo(() => {
        const types = Array.isArray(notificationTypes) ? notificationTypes : [notificationTypes];
        return Array.from(new Set(types));
    }, [notificationTypes]);

    const clearLastNotification = () => setLastNotification(null);

    useEffect(() => {
        if (!socket || normalizedTypes.length === 0) return;

        const handler = (payload: TPayload) => {



            onNotificationArrivedRef.current?.(payload);
            setLastNotification(payload);

            if (!disableSnackbarAlertRef.current) {
                const message = buildSnackbarMessageRef.current?.(payload) ?? "";
                if (message) showSnackbar(message, "info");
            }
        };

        normalizedTypes.forEach((type) => socket.on(type, handler));

        return () => {
            normalizedTypes.forEach((type) => socket.off(type, handler));
        };
    }, [socket, normalizedTypes, showSnackbar]);

    return { lastNotification, clearLastNotification };
}